import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable, NotifyProperty, nameof } from "../../shared/bindable-data";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { AuthConstants } from "../scenes/popups/login/AuthConstants";
import { ILogger } from "../logger/ILogger";
import { IConnectionModel } from "./IConnectionModel";
import { INetworkClient } from "./INetworkClient";
import { ObjectFieldsChecker } from "by/gamefactory/tools/ObjectFieldsChecker";

export const AuthTokenDisconnectionState = "disconnected";

@injectable()
export class ConnectionModel extends Bindable implements IConnectionModel {
    private static readonly DISCONNECTION_ERROR_CODE = 401;

    public readonly connected: NotifyProperty<boolean>;

    private readonly networkClient: INetworkClient;
    private readonly variableSaver: VariableSaver;
    private readonly logger: ILogger;

    private networkError: Error;

    private get authTokenCleared(): boolean {
        if (!this.variableSaver.exist(AuthConstants.ACCESS_TOKEN)) {
            return true;
        }
        const authToken = this.variableSaver.getString(AuthConstants.ACCESS_TOKEN);
        return authToken === "" || authToken === AuthTokenDisconnectionState;
    }

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        variableSaver: VariableSaver,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.variableSaver = variableSaver;
        this.logger = logger;
        this.connected = new NotifyProperty(this, nameof(this, "connected"), !this.authTokenCleared);
        this.networkClient.onError.add(this.onNetworkClientError, this);
    }

    public resolveConnection(): void {
        this.networkError = null;
        this.updateConnection();
    }

    public disconnect(): void {
        if (!this.connected.value) {
            return;
        }
        this.networkError = new Error("Disconnect requested");
        this.variableSaver.saveString(AuthConstants.ACCESS_TOKEN, AuthTokenDisconnectionState);
        this.variableSaver.saveString(AuthConstants.REFRESH_TOKEN, "");
        this.networkClient.removeAuthorizationHeader();
        this.updateConnection();
    }

    private onNetworkClientError(error: any): void {
        this.updateConnection();
        if (!this.connected.value) {
            return;
        }
        if (!ObjectFieldsChecker.checkFields(error, ["response"])) {
            this.logger.error(`[ConnectionModel] Unknown error, connection not affected: `, error);
            return;
        }
        const response = error.response;
        if (response.statuscode === ConnectionModel.DISCONNECTION_ERROR_CODE || response.status === ConnectionModel.DISCONNECTION_ERROR_CODE) {
            this.logger.debug(`[ConnectionModel] Unauthorized error raised from network, disconnected`);
            this.networkError = error;
            this.updateConnection();
        } else {
            this.logger.debug(`[ConnectionModel] Unhandled error raised from network, ignored: ${error}`);
        }
    }

    private updateConnection(): void {
        if (this.networkError != null) {
            this.connected.setValue(false);
            return;
        }
        this.connected.setValue(!this.authTokenCleared);
    }
}
